import { useEffect, useState } from 'react';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { NavBar } from '@/components/ui/nav-bar';
import type { NavItem } from '@/components/ui/sidebar';
import { SideBar } from '@/components/ui/sidebar';
import { useAuth } from '@/context/auth-context';
import { useBreadcrumb } from '@/hooks/use-breadcrumb';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
  navigation: (NavItem & { code?: string })[];
  className?: string;
  mainClassName?: string;
  navbarTitle?: string;
}

export function AppLayout(props: LayoutProps) {
  const { navBarItems } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    }
    return false;
  });

  // Listen for localStorage changes (from sidebar toggle)
  useEffect(() => {
    const handleStorage = () => {
      setSidebarCollapsed(localStorage.getItem('sidebar-collapsed') === 'true');
    };

    // Custom event for same-tab updates
    const handleSidebarChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebar-collapsed') === 'true');
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('sidebar-collapsed-change', handleSidebarChange);

    // Also poll for changes since storage event doesn't fire in same tab
    const interval = setInterval(() => {
      const current = localStorage.getItem('sidebar-collapsed') === 'true';
      if (current !== sidebarCollapsed) {
        setSidebarCollapsed(current);
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('sidebar-collapsed-change', handleSidebarChange);
      clearInterval(interval);
    };
  }, [sidebarCollapsed]);

  const { children, navigation, className, mainClassName } = props;
  // Generate breadcrumbs from navigation
  const breadcrumbs = useBreadcrumb({ navigation });

  return (
    <div className="h-full">
      <SideBar navigation={navigation} menuListItem={navBarItems} navbarTitle={props.navbarTitle} />

      <div
        className={cn(
          'h-full transition-all duration-300',
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72',
          className,
        )}
      >
        <div className="hidden lg:block">
          <NavBar menuListItem={navBarItems} />
        </div>

        <main className="h-full">
          {/* Breadcrumb section */}
          {breadcrumbs.length > 0 && (
            <div className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
              <div className="px-4 sm:px-6 lg:px-8">
                <Breadcrumb items={breadcrumbs} className="py-3" />
              </div>
            </div>
          )}

          <div className={cn('px-4 sm:px-6 lg:px-8 h-full pt-6', mainClassName)}>{children}</div>
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
